import React, { useEffect, useState } from "react"
import axios from "axios"
import { CustomRoutes, NotFound, Resource } from "react-admin"
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn"

import Providers from "providers/Providers"
import UserService from "UserService"

import users from "user"
import invoices from "invoice"
import featureSwitches from "featureSwitch"
import featureParameters from "featureParameter"
import PricingRuleList from "pricingRule/PricingRuleList"
import companies from "company"
import gatedCommunities from "gatedCommunity"
import jobs from "job"
import JobList from "job/JobList"
import stages from "stage"
import assignments from "assignment"
import assets from "assets"
import subscriptions from "subscription"
import { Route } from "react-router-dom"
import RCAList from "job/RCAList"
import SubAssetsImport from "assets/SubAssets"
import subassets from "subassets"
import BiPage from "bi/BiPage"
import MultiLangJobShow from "multilangJob/MultiLangJobShow"
import { TagEdit, TagCreate } from "./tags/TagCRUD"
import { TagList } from "./tags/TagList"
const contentType = "Content-Type"
const header = "application/json"

axios.defaults.headers.post[contentType] = header
axios.defaults.headers.get[contentType] = header
axios.defaults.headers.put[contentType] = header
axios.interceptors.request.use((config) =>
  UserService.unauthenticatedPath()
    ? Promise.resolve(config)
    : new Promise((resolve) =>
      UserService.updateToken(async function () {
        const token = await UserService.getToken()
        if (token && config && config.headers) config.headers.Authorization = `Bearer ${token}`
        resolve(config)
      })
    )
)

const App = () => {
  const [isAssetManagerExist, setIsAssetManagerExist] = useState<boolean | null>(null)

  useEffect(() => {
    const checkIfAssetManagerExists = async () => {
      axios.head("asset-manager/assets?sourceLang=EN_US&targetLang=JA").then((result) => {
        setIsAssetManagerExist(result.status == 200)
      }).catch((error) => {
        setIsAssetManagerExist(false)
      })
    }
    checkIfAssetManagerExists()
  }, [isAssetManagerExist])

  if (isAssetManagerExist === null) return null

  return (
    <Providers>
      {UserService.canAccess() && (
        <>
          {UserService.isGcProjectManagerOnly() && (
            <Resource name="jobs" list={JobList} options={{ label: "Projects" }} />
          )}
          {UserService.isAnyManager() &&
            <>
              <Resource name="jobs" {...jobs} options={{ label: "Projects" }}>
                <Route path=":jobId/rcas" element={<RCAList />} />
              </Resource>
              <Resource name="users" {...users} />
              <Resource name="companies" {...companies} />
              <Resource name="gatedCommunities" {...gatedCommunities} options={{ label: "Gated Communities" }} />
              <Resource name="stages" {...stages} />
              <Resource name="multilang-jobs" show={MultiLangJobShow} />
              <Resource name="assignments" {...assignments} />
              <Resource name="skills" />
            </>}
          {UserService.hasRoles(["ROLE_PLATFORM_MANAGER", "ROLE_SUPPORT", "ROLE_COMMUNITY_MANAGER"]) && (
            <Resource name="invoices" {...invoices} />
          )}
          {UserService.hasRoles(["ROLE_SUPPORT", "ROLE_PLATFORM_MANAGER"]) && (
            <Resource name="subscriptions" {...subscriptions} />
          )}
          {UserService.hasRoles(["ROLE_PLATFORM_MANAGER", "ROLE_SUPPORT"]) && (
            <Resource
              name="pricingrules/dynamic"
              icon={MonetizationOnIcon}
              list={(props: any) => <PricingRuleList {...props} type="DYNAMIC" />}
              options={{ label: "Dynamic Bundle Prices" }}
            />
          )}
          {UserService.hasRoles(["ROLE_SUPPORT"]) && (
            <Resource
              name="pricingrules/fixed"
              icon={MonetizationOnIcon}
              list={(props: any) => <PricingRuleList {...props} type="FIXED" />}
              options={{ label: "Fixed Bundle Prices" }}
            />
          )}
          {UserService.hasRoles([]) && (
            <Resource name="features/switches" {...featureSwitches} options={{ label: "Feature Switches" }} />
          )}
          {UserService.hasRoles([]) && (
            <Resource name="features/parameters" {...featureParameters} options={{ label: "Feature Parameters" }} />
          )}
          {UserService.hasRoles(["ROLE_LANGUAGE_MINER"]) && isAssetManagerExist && (
            <Resource name="assets" {...assets} options={{ label: "Language Miner" }} />
          )}
          {UserService.hasRoles(["ROLE_LANGUAGE_MINER"]) && isAssetManagerExist && (
            <Resource name="subassets" {...subassets} options={{ label: "Language Miner" }} />
          )}
          <CustomRoutes>
            {UserService.hasRoles(["ROLE_LANGUAGE_MINER"]) && isAssetManagerExist && (
              <Route path="subassets/import" element={<SubAssetsImport />} />
            )}
            <Route path="/bi" element={<BiPage />} />
          </CustomRoutes>
          {!UserService.isGcProjectManagerOnly() && (
            <Resource name="tags" list={TagList} edit={TagEdit} create={TagCreate} />
          )}
        </>
      )}
      {!UserService.canAccess() && (
        <CustomRoutes>
          <Route path="" element={<NotFound />} />
        </CustomRoutes>
      )}
    </Providers>
  )
}

export default App
